// src/utils/pwaDetection.js
// Utilities for detecting PWA installation and generating device fingerprints

/**
 * Check if the app is running as an installed PWA
 * @returns {boolean}
 */
export const isPWAInstalled = () => {
  // Check if running in standalone mode (installed PWA)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

  // iOS Safari specific check
  const isIOSStandalone = window.navigator.standalone === true;

  // Check if app was added to home screen (Android)
  const isAndroidStandalone = window.matchMedia('(display-mode: standalone)').matches;

  return isStandalone || isIOSStandalone || isAndroidStandalone;
};

/**
 * Check if the browser supports PWA features
 * @returns {boolean}
 */
export const isPWASupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

/**
 * Get display mode (standalone, browser, etc.)
 * @returns {string}
 */
export const getDisplayMode = () => {
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return 'standalone';
  }
  if (window.matchMedia('(display-mode: fullscreen)').matches) {
    return 'fullscreen';
  }
  if (window.matchMedia('(display-mode: minimal-ui)').matches) {
    return 'minimal-ui';
  }
  if (window.navigator.standalone === true) {
    return 'standalone-ios';
  }
  return 'browser';
};

/**
 * Generate a device fingerprint for device identification
 * This creates a semi-unique identifier based on device characteristics
 * @returns {Promise<string>}
 */
export const generateDeviceFingerprint = async () => {
  const components = [];

  // Screen resolution
  components.push(`${window.screen.width}x${window.screen.height}`);
  components.push(`${window.screen.colorDepth}`);

  // Timezone
  components.push(new Date().getTimezoneOffset());

  // Language
  components.push(navigator.language);

  // Platform
  components.push(navigator.platform);

  // Hardware concurrency (CPU cores)
  if (navigator.hardwareConcurrency) {
    components.push(navigator.hardwareConcurrency);
  }

  // Device memory (if available)
  if (navigator.deviceMemory) {
    components.push(navigator.deviceMemory);
  }

  // User agent (simplified)
  components.push(navigator.userAgent.substring(0, 50));

  // Canvas fingerprint (for additional uniqueness)
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(0, 0, 140, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('CARO Portal', 2, 2);
    components.push(canvas.toDataURL());
  } catch (e) {
    // Canvas fingerprinting might be blocked
    components.push('canvas-blocked');
  }

  // Combine all components and hash them
  const fingerprint = components.join('|');

  // Use SubtleCrypto to create a hash
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(fingerprint);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  } catch (e) {
    // Fallback to simple hash if SubtleCrypto is not available
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(16);
  }
};

/**
 * Get a user-friendly device name
 * @returns {string}
 */
export const getDeviceName = () => {
  const ua = navigator.userAgent;

  // Detect iOS devices
  if (/iPhone/.test(ua)) {
    return 'iPhone';
  }
  if (/iPad/.test(ua)) {
    return 'iPad';
  }

  // Detect Android devices
  if (/Android/.test(ua)) {
    const match = ua.match(/Android ([0-9.]+)/);
    return match ? `Android ${match[1]}` : 'Android Device';
  }

  // Detect Mac
  if (/Mac OS X/.test(ua)) {
    return 'Mac';
  }

  // Detect Windows
  if (/Windows/.test(ua)) {
    return 'Windows PC';
  }

  // Detect Linux
  if (/Linux/.test(ua)) {
    return 'Linux';
  }

  // Get browser name
  let browserName = 'Unknown Browser';
  if (/Chrome/.test(ua) && !/Edge/.test(ua)) {
    browserName = 'Chrome';
  } else if (/Safari/.test(ua) && !/Chrome/.test(ua)) {
    browserName = 'Safari';
  } else if (/Firefox/.test(ua)) {
    browserName = 'Firefox';
  } else if (/Edge/.test(ua)) {
    browserName = 'Edge';
  }

  return `${browserName} on ${navigator.platform}`;
};

/**
 * Get device information object
 * @returns {Promise<object>}
 */
export const getDeviceInfo = async () => {
  const fingerprint = await generateDeviceFingerprint();

  return {
    isPWA: isPWAInstalled(),
    displayMode: getDisplayMode(),
    deviceFingerprint: fingerprint,
    deviceName: getDeviceName(),
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    colorDepth: window.screen.colorDepth,
    timestamp: new Date().toISOString()
  };
};

/**
 * Store device info in storage (for later retrieval)
 * @returns {Promise<void>}
 */
export const storeDeviceInfo = async () => {
  try {
    const deviceInfo = await getDeviceInfo();

    // Use IndexedDB if available, otherwise localStorage
    if (typeof indexedDB !== 'undefined') {
      const { setItem } = await import('./indexedDB');
      await setItem('device_info', deviceInfo);
    } else {
      localStorage.setItem('device_info', JSON.stringify(deviceInfo));
    }

    console.log('Device info stored:', deviceInfo);
  } catch (error) {
    console.error('Failed to store device info:', error);
  }
};

/**
 * Get stored device info
 * @returns {Promise<object|null>}
 */
export const getStoredDeviceInfo = async () => {
  try {
    // Try IndexedDB first
    if (typeof indexedDB !== 'undefined') {
      const { getItem } = await import('./indexedDB');
      const deviceInfo = await getItem('device_info');
      if (deviceInfo) return deviceInfo;
    }

    // Fallback to localStorage
    const stored = localStorage.getItem('device_info');
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Failed to get stored device info:', error);
    return null;
  }
};

/**
 * Listen for PWA installation
 * @param {Function} callback - Callback when PWA is installed
 * @returns {Function} - Cleanup function
 */
export const onPWAInstalled = (callback) => {
  let deferredPrompt = null;

  const beforeInstallHandler = (e) => {
    // Prevent the mini-infobar from appearing on mobile
    e.preventDefault();
    // Store the event for later use
    deferredPrompt = e;
  };

  const appInstalledHandler = () => {
    console.log('PWA was installed');
    deferredPrompt = null;
    callback({ installed: true });
  };

  window.addEventListener('beforeinstallprompt', beforeInstallHandler);
  window.addEventListener('appinstalled', appInstalledHandler);

  // Return cleanup function
  return () => {
    window.removeEventListener('beforeinstallprompt', beforeInstallHandler);
    window.removeEventListener('appinstalled', appInstalledHandler);
  };
};

// Export default object with all methods
export default {
  isPWAInstalled,
  isPWASupported,
  getDisplayMode,
  generateDeviceFingerprint,
  getDeviceName,
  getDeviceInfo,
  storeDeviceInfo,
  getStoredDeviceInfo,
  onPWAInstalled
};
